import React, { useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { CartProvider } from "@/context/CartContext";
import { AuthProvider } from "@/context/AuthContext";
import { ProductProvider } from "@/context/ProductContext";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/common/ScrollToTop";
import ScrollRestoration from "@/components/common/ScrollRestoration";
import CartDrawer from "@/components/cart/CartDrawer";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { toast } from 'sonner';
import Home from "@/pages/Home";
import Contact from "@/pages/Contact";
import FAQ from "@/pages/FAQ";
import Legal from "@/pages/Legal";
import Shop from "@/pages/Shop";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminLogin from "@/pages/AdminLogin";
import Profile from "@/pages/Profile";
import Checkout from "@/pages/Checkout";
import CheckoutSignIn from "@/pages/CheckoutSignIn";
import GuestComplaints from "@/components/common/GuestComplaints";
import ProtectedRoute, { PublicOnlyRoute } from "@/components/common/ProtectedRoute";
import AgeDeclarationModal from "@/components/common/AgeDeclarationModal";

// Layout wrapper to conditionally show header/footer
const Layout = ({ children }) => {
  const location = useLocation();
  const hideLayout = ['/login', '/register', '/admin'].includes(location.pathname);

  return (
    <>
      <ScrollRestoration />
      {!hideLayout && <Header />}
      {!hideLayout && <CartDrawer />}
      {children}
      {!hideLayout && <Footer />}
      {!hideLayout && <ScrollToTop />}
    </>
  );
};

function App() {

  useEffect(() => {
    const handleSessionExpired = (e) => {
      const msg = e?.detail?.message || 'Your session has expired. Please log in again.';
      toast.error(msg, {
        duration: 4000,
        id: 'session-expired',
      });
    };

    window.addEventListener('session:expired', handleSessionExpired);
    return () => window.removeEventListener('session:expired', handleSessionExpired);
  }, []);

  return (
    <ErrorBoundary>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
            <AgeDeclarationModal />
            <ProductProvider>
              <CartProvider>
                <Layout>
                  <Routes>
                    <Route path="/" element={<Navigate to="/shop" replace />} />
                    <Route path="/home" element={<Home />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route path="/legal" element={<Legal />} />
                    <Route path="/shop" element={<Shop />} />
                    <Route path="/login" element={
                      <PublicOnlyRoute>
                        <Login />
                      </PublicOnlyRoute>
                    } />
                    <Route path="/register" element={
                      <PublicOnlyRoute>
                        <Register />
                      </PublicOnlyRoute>
                    } />
                    <Route path="/admin/login" element={
                      <PublicOnlyRoute redirectTo="/admin">
                        <AdminLogin />
                      </PublicOnlyRoute>
                    } />
                    <Route path="/admin" element={
                      <ProtectedRoute requireAdmin={true} redirectTo="/admin/login">
                        <AdminDashboard />
                      </ProtectedRoute>
                    } />
                    <Route path="/profile" element={
                      <ProtectedRoute>
                        <Profile />
                      </ProtectedRoute>
                    } />
                    <Route path="/complaints/guest" element={
                      <GuestComplaints />
                    } />
                    <Route path="/checkout" element={
                      <Checkout />
                    } />
                    <Route path="/checkout/sign-in" element={
                      <CheckoutSignIn />
                    } />
                  </Routes>
                </Layout>
                <Toaster position="top-right" offset="80px" />
              </CartProvider>
            </ProductProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}

export default App;
